import {
  convertCouponInputToRequest,
  convertPauseInputToRequest,
  type CouponInputFormik,
  type PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useServicesContext } from "@modules/core/services/services.context"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { type ProgramBundleSubscriberDto } from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberDto"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { type ProgramSubscriberDto } from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { useInfiniteQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export enum UseProgramSubscriberDetailsKey {
  PROGRAM_SUBSCRIBER = "programSubscriber",
  PROGRAM_BOX_SUBSCRIBER = "programBoxSubscriber",
  PROGRAM_BUNDLE_SUBSCRIBER = "programBundleSubscriber",
}

export const useProgramSubscriberDetails = (
  key: UseProgramSubscriberDetailsKey,
  programId: string,
  subscriberId: string,
) => {
  const { programSubscriberApi, programBundleSubscriberApi, programBoxSubscriberApi } = useServicesContext()
  const subscriberQueryKey = [key, programId, subscriberId]

  const { data, isLoading, refetch, error } = useSuspenseQuery({
    queryKey: subscriberQueryKey,
    queryFn: async () => {
      if (key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER) {
        const result = await programBundleSubscriberApi.getProgramBundleSubscriber(programId, subscriberId)
        return ProgramBundleSubscriberEntity.fromItemDto(result)
      } else if (key === UseProgramSubscriberDetailsKey.PROGRAM_BOX_SUBSCRIBER) {
        const result = await programBoxSubscriberApi.getProgramSubscriber(programId, subscriberId)
        return ProgramSubscriberEntity.fromDto(result)
      } else {
        const result = await programSubscriberApi.getProgramSubscriber(programId, subscriberId)
        return ProgramSubscriberEntity.fromDto(result)
      }
    },
  })

  return {
    data,
    isLoading,
    refetch,
    error,
  }
}

export const useProgramSubscribersSearchOnlyGyms = (programId: string, searchTerm?: string) => {
  const { programBoxSubscriberApi } = useServicesContext()

  const { data, isLoading, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["programBoxSubscribersSearch", programId, searchTerm?.toLocaleLowerCase()],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      return programBoxSubscriberApi.searchProgramSubscribersOnlyGyms(
        programId,
        pageParam,
        40,
        searchTerm?.toLocaleLowerCase(),
      )
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.totalPages - 1) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const subscriberList = useMemo(
    () => data?.pages.flatMap((page) => page.content).map((content) => ProgramSubscriberEntity.fromDto(content)) ?? [],
    [data],
  )
  return {
    data: {
      ...data,
      content: subscriberList,
      totalSize: data?.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

export const useProgramSubscribers = (programId: string, searchTerm?: string) => {
  const { programSubscriberApi } = useServicesContext()

  const { data, isLoading, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["programSubscribersSearch", programId, searchTerm?.toLocaleLowerCase()],
    queryFn: async ({ pageParam }: { pageParam: number }) => {
      return programSubscriberApi.searchProgramSubscribers(programId, pageParam, 40, searchTerm?.toLocaleLowerCase())
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.page >= lastPage.totalPages - 1) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const subscriberList = useMemo(
    () => data?.pages.flatMap((page) => page.content).map((content) => ProgramSubscriberEntity.fromDto(content)) ?? [],
    [data],
  )

  return {
    data: {
      ...data,
      content: subscriberList,
      totalSize: data?.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    refetch,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  }
}

export type ProgramSubscriberActions = {
  updatePause: (pause: PauseInputFormik) => Promise<void>
  resumeSubscription: () => Promise<void>
  isResumeSubscriptionLoading: boolean
  updateInternalNote: (note: string) => Promise<ProgramSubscriberEntity | ProgramBundleSubscriberEntity>
  isUpdateInternalNote: boolean
  removeSubscriber: (cancelAtPeriodEnd: boolean) => Promise<void>
  applySubscriptionDiscount: (coupon: CouponInputFormik) => Promise<void>
  isApplyingSubscriptionDiscount: boolean
  removeSubscriptionDiscount: () => Promise<void>
  isRemovingSubscriptionDiscount: boolean
}

export const useProgramSubscriberActions = (
  programId: string,
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity,
): ProgramSubscriberActions => {
  const { programSubscriberApi, programBoxSubscriberApi, programBundleSubscriberApi } = useServicesContext()
  const queryClient = useQueryClient()
  const key =
    subscriberDetails instanceof ProgramBundleSubscriberEntity
      ? UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
      : subscriberDetails.box
        ? UseProgramSubscriberDetailsKey.PROGRAM_BOX_SUBSCRIBER
        : UseProgramSubscriberDetailsKey.PROGRAM_SUBSCRIBER

  const subscriberId =
    subscriberDetails instanceof ProgramBundleSubscriberEntity
      ? subscriberDetails.programBundleSubscriptionId
      : subscriberDetails.box
        ? subscriberDetails.box.id
        : (subscriberDetails.id ?? "")

  const api =
    key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
      ? programBundleSubscriberApi
      : key === UseProgramSubscriberDetailsKey.PROGRAM_BOX_SUBSCRIBER
        ? programBoxSubscriberApi
        : programSubscriberApi
  const subscriberQueryKey =
    key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
      ? ["programBundleSubscriber", programId, subscriberId]
      : key === UseProgramSubscriberDetailsKey.PROGRAM_BOX_SUBSCRIBER
        ? ["programBoxSubscriber", programId, subscriberId]
        : ["programSubscriber", programId, subscriberId]
  const subscribersQueryKey =
    key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
      ? ["programBundleSubscribers", programId]
      : ["programSubscribers", programId]
  const subscribersSearchQueryKey =
    key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
      ? ["programBundleSubscribers", programId]
      : key === UseProgramSubscriberDetailsKey.PROGRAM_BOX_SUBSCRIBER
        ? ["programBoxSubscribersSearch", programId]
        : ["programSubscribersSearch", programId]

  // Resume subscription
  const { mutateAsync: resumeSubscription, isPending: isResumeSubscriptionLoading } = useMutation({
    mutationFn: () => {
      return api.resumeSubscription(programId, subscriberId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  // Pause subscription
  const { mutateAsync: updatePause } = useMutation({
    mutationFn: (pause: PauseInputFormik) => {
      return api.updateSubscriberPause(programId, subscriberId, convertPauseInputToRequest(pause))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  // Update internal note
  const { mutateAsync: updateInternalNote, isPending: isUpdateInternalNote } = useMutation({
    mutationFn: async (note: string) => {
      const data = await api.updateSubscriberInternalNote(programId, subscriberId, note)
      return key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER
        ? ProgramBundleSubscriberEntity.fromItemDto(data as ProgramBundleSubscriberDto)
        : ProgramSubscriberEntity.fromDto(data as ProgramSubscriberDto)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriberQueryKey, data)
    },
  })

  // Remove subscriber
  const { mutateAsync: removeSubscriber } = useMutation({
    mutationFn: (cancelAtPeriodEnd: boolean) => {
      return api.removeSubscriber(programId, subscriberId, cancelAtPeriodEnd)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscribersQueryKey,
      })
      queryClient.removeQueries({
        queryKey: subscriberQueryKey,
      })
      queryClient.removeQueries({
        queryKey: subscribersSearchQueryKey,
      })
    },
  })

  // Add subscription discount
  const { mutateAsync: applySubscriptionDiscount, isPending: isApplyingSubscriptionDiscount } = useMutation({
    mutationFn: (coupon: CouponInputFormik) => {
      return api.applySubscriptionDiscount(programId, subscriberId, convertCouponInputToRequest(coupon))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  // Remove subscription discount
  const { mutateAsync: removeSubscriptionDiscount, isPending: isRemovingSubscriptionDiscount } = useMutation({
    mutationFn: () => {
      return api.removeSubscriptionDiscount(programId, subscriberId)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  return {
    updatePause,
    resumeSubscription,
    isResumeSubscriptionLoading,
    updateInternalNote,
    isUpdateInternalNote,
    removeSubscriber,
    applySubscriptionDiscount,
    isApplyingSubscriptionDiscount,
    removeSubscriptionDiscount,
    isRemovingSubscriptionDiscount,
  }
}
